import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaBookOpen, FaDownload, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const ITEMS_PER_PAGE = 10;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function SupervisorWorkDiaryDashboard() {
  const navigate = useNavigate();
  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedDiary, setSelectedDiary] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const totalPages = useMemo(() => Math.ceil(total / ITEMS_PER_PAGE) || 1, [total]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const supervisorFetch = async (url, options = {}) => {
    const token = localStorage.getItem('gogig_supervisor_access');
    if (!token) {
      throw new Error('Supervisor session expired. Please log in again.');
    }
    return fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      }
    });
  };

  const fetchWorkDiaries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: ITEMS_PER_PAGE,
        search: search.trim()
      });
      const res = await supervisorFetch(`${API_URL}/api/supervisor/work-diaries?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setDiaries(data.diaries || []);
        setTotal(data.total || 0);
      } else {
        toast.error(data.message || 'Failed to load work diaries');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load work diaries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkDiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const openDiary = async (diary) => {
    try {
      const res = await supervisorFetch(`${API_URL}/api/supervisor/work-diaries/${diary.id}`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load diary');
      }
      setSelectedDiary(data.diary);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to fetch diary');
    }
  };

  const handleExport = async (diaryId, candidateName) => {
    try {
      // Reuse admin export format on server side? For supervisors we can open detail and let admin export. If the server restricts, skip export.
      // Attempt admin export path will fail for supervisors; so we simply render PDF export as not available.
      toast.error('Export is available from Admin panel only.');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to export');
    }
  };

  const handleSignEntry = async (entryId) => {
    try {
      const res = await supervisorFetch(`${API_URL}/api/supervisor/work-diaries/entries/${entryId}/sign`, {
        method: 'PATCH'
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to sign entry');
      }
      toast.success('Entry signed');
      // Refresh selected diary to show updated signature
      if (selectedDiary) {
        await openDiary(selectedDiary);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to sign entry');
    }
  };

  return (
    <div style={{ width: '100%', padding: isMobile ? 12 : 20 }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: 24, 
        gap: 16 
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 8 : 16,
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto'
        }}>
          <button
            onClick={() => navigate('/supervisor')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '8px 12px' : '10px 16px',
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              cursor: 'pointer',
              color: '#374151',
              fontSize: isMobile ? 12 : 14,
              fontWeight: 600,
              transition: 'all 0.2s ease',
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e5e7eb';
              e.target.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f3f4f6';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            <FaArrowLeft style={{ marginRight: 8 }} />
            {isMobile ? 'Back' : 'Back to Dashboard'}
          </button>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 28, 
            fontWeight: 800, 
            color: '#16a34a', 
            letterSpacing: 0.5, 
            margin: 0,
            textAlign: isMobile ? 'center' : 'left',
            width: isMobile ? '100%' : 'auto'
          }}>
            {isMobile ? 'Work Diary' : 'Work Diary – Supervisor View'}
          </h2>
        </div>
        <form 
          onSubmit={handleSearchSubmit} 
          style={{ 
            display: 'flex', 
            gap: 12, 
            alignItems: 'center',
            width: isMobile ? '100%' : 'auto',
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <div style={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
            <FaSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={isMobile ? "Search..." : "Search candidate name, email or mobile"}
              className="gogig-input"
              style={{ 
                paddingLeft: 40, 
                minWidth: isMobile ? '100%' : 280,
                width: isMobile ? '100%' : 'auto'
              }}
            />
          </div>
          <button 
            type="submit" 
            className="gogig-card-btn"
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            Search
          </button>
        </form>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 2px 16px rgba(34,197,94,0.08)', overflow: 'hidden' }}>
        {isMobile ? (
          // Mobile Card View
          <div style={{ padding: isMobile ? 12 : 0 }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading work diaries...</div>
            ) : diaries.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>No work diaries found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {diaries.map((diary) => (
                  <div 
                    key={diary.id} 
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: 16,
                      background: '#fff'
                    }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, color: '#111827', fontSize: 16, marginBottom: 4 }}>
                        {diary.Candidate?.name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>
                        {diary.Candidate?.email}
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>
                        {diary.Candidate?.mobile}
                      </div>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: 12, 
                      marginBottom: 12,
                      paddingTop: 12,
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Trade</div>
                        <div style={{ fontSize: 14, color: '#1f2937', fontWeight: 600 }}>{diary.trade || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Entries</div>
                        <div style={{ fontSize: 14, color: '#1f2937', fontWeight: 600 }}>{diary.entry_count || 0}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Last Entry</div>
                        <div style={{ fontSize: 14, color: '#1f2937' }}>{formatDate(diary.last_entry_date)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Next Due</div>
                        <div style={{ fontSize: 14, color: diary.next_due_date ? '#b91c1c' : '#1f2937' }}>
                          {diary.next_due_date ? formatDate(diary.next_due_date) : 'Not scheduled'}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                      <button
                        className="gogig-card-btn-outline"
                        onClick={() => openDiary(diary)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: 6,
                          width: '100%',
                          padding: '10px'
                        }}
                      >
                        <FaBookOpen /> View
                      </button>
                      <button
                        className="gogig-card-btn"
                        onClick={() => handleExport(diary.id, diary.Candidate?.name)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: 6,
                          width: '100%',
                          padding: '10px'
                        }}
                      >
                        <FaDownload /> Export
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#1f2937' }}>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Candidate</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Trade</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Entries</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Last Entry</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Next Due</th>
                  <th style={{ padding: '14px 16px', fontWeight: 700 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading work diaries...</td>
                  </tr>
                ) : diaries.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>No work diaries found.</td>
                  </tr>
                ) : (
                  diaries.map((diary) => (
                    <tr key={diary.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{diary.Candidate?.name || 'Unknown'}</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{diary.Candidate?.email}</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{diary.Candidate?.mobile}</div>
                      </td>
                      <td style={{ padding: '14px 16px', color: '#1f2937' }}>{diary.trade || '-'}</td>
                      <td style={{ padding: '14px 16px', color: '#1f2937', fontWeight: 600 }}>{diary.entry_count || 0}</td>
                      <td style={{ padding: '14px 16px', color: '#1f2937' }}>{formatDate(diary.last_entry_date)}</td>
                      <td style={{ padding: '14px 16px', color: diary.next_due_date ? '#b91c1c' : '#1f2937' }}>
                        {diary.next_due_date ? formatDate(diary.next_due_date) : 'Not scheduled'}
                      </td>
                      <td style={{ padding: '14px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          className="gogig-card-btn-outline"
                          onClick={() => openDiary(diary)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <FaBookOpen /> View
                        </button>
                        <button
                          className="gogig-card-btn"
                          onClick={() => handleExport(diary.id, diary.Candidate?.name)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <FaDownload /> Export
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: isMobile ? '12px' : '16px 20px', 
          borderTop: '1px solid #e5e7eb', 
          gap: 12 
        }}>
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Page {page} of {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
            <button
              className="gogig-card-btn-outline"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ flex: isMobile ? 1 : 'none' }}
            >
              Previous
            </button>
            <button
              className="gogig-card-btn-outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ flex: isMobile ? 1 : 'none' }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedDiary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? 12 : 20,
            padding: isMobile ? 16 : 28,
            width: '95%',
            maxWidth: 900,
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              marginBottom: 20,
              gap: 12
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? 18 : 24, color: '#16a34a' }}>
                  {selectedDiary.Candidate?.name || 'Candidate'} – Work Diary
                </h3>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                  {selectedDiary.establishment_name} · {selectedDiary.trade}
                </div>
              </div>
              <button
                onClick={() => setSelectedDiary(null)}
                style={{ 
                  fontSize: 28, 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: '#6b7280',
                  padding: 0,
                  alignSelf: isMobile ? 'flex-end' : 'auto'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ 
              marginBottom: 20, 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', 
              gap: 16 
            }}>
              <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Apprenticeship Code</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>{selectedDiary.apprenticeship_code || '-'}</div>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Establishment</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>{selectedDiary.establishment_name || '-'}</div>
              </div>
            </div>

            {isMobile ? (
              // Mobile Card View for Entries
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(selectedDiary.entries || []).length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No entries recorded yet.</div>
                ) : (
                  selectedDiary.entries.map((entry) => (
                    <div 
                      key={entry.id} 
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: 16,
                        background: '#fff'
                      }}
                    >
                      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Serial</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>{entry.serial_number}</div>
                      </div>
                      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Week</div>
                        <div style={{ fontSize: 14, color: '#1f2937' }}>
                          {formatDate(entry.week_start)} – {formatDate(entry.week_end)}
                        </div>
                      </div>
                      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Job / Work Done</div>
                        <div style={{ fontSize: 14, color: '#1f2937', whiteSpace: 'pre-wrap' }}>{entry.job_work_done}</div>
                      </div>
                      <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Supervisor</div>
                        <div style={{ fontSize: 14, color: '#1f2937' }}>{entry.supervisor_signature || '-'}</div>
                      </div>
                      {!entry.supervisor_signature && (
                        <button
                          className="gogig-card-btn-outline"
                          onClick={() => handleSignEntry(entry.id)}
                          style={{ width: '100%', padding: '10px' }}
                        >
                          Mark Signed
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Desktop Table View
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr style={{ background: '#ecfdf5', color: '#065f46' }}>
                      <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Serial</th>
                      <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Week</th>
                      <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Job / Work Done</th>
                      <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Supervisor</th>
                      <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedDiary.entries || []).length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No entries recorded yet.</td>
                      </tr>
                    ) : (
                      selectedDiary.entries.map((entry) => (
                        <tr key={entry.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '12px 10px', fontWeight: 600 }}>{entry.serial_number}</td>
                          <td style={{ padding: '12px 10px' }}>{formatDate(entry.week_start)} – {formatDate(entry.week_end)}</td>
                          <td style={{ padding: '12px 10px', whiteSpace: 'pre-wrap' }}>{entry.job_work_done}</td>
                          <td style={{ padding: '12px 10px' }}>{entry.supervisor_signature || '-'}</td>
                          <td style={{ padding: '12px 10px' }}>
                            {!entry.supervisor_signature && (
                              <button
                                className="gogig-card-btn-outline"
                                onClick={() => handleSignEntry(entry.id)}
                              >
                                Mark Signed
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SupervisorWorkDiaryDashboard;


